import { buildModalRoute } from '@manager/modules/modals/routable/modal-routable-utils';
import { routeNames } from '@manager/router/route-names';
import type { RouteMetaConfig } from '@manager/types/router';
import type { RouteLocationRaw } from 'vue-router';

export const customerRoutes: RouteMetaConfig[] = [
  {
    path: '/customers',
    name: routeNames.customerOverview,
    component: () => import('./views/CustomerListView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'customers.view',
      listFilterName: 'customers',
    },
    children: [
      buildModalRoute(
        {
          name: routeNames.customerCreate,
          path: 'create',
          meta: {
            requiresAuth: true,
            permission: 'customers.create',
          },
          component: () => import('./views/CustomerCreateView.vue'),
        },
        { fullscreen: true }
      ),
      buildModalRoute(
        {
          path: 'export',
          name: routeNames.customerExport,
          meta: {
            requiresAuth: true,
            permission: 'customers.export',
          },
          component: () => import('./views/CustomerExportView.vue'),
        },
        { fullscreen: false, width: 'unset' }
      ),
    ],
  },
  {
    path: '/customers/:customerId(\\d+)',
    name: routeNames.customerDetails,
    component: () => import('./views/CustomerDetailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'customers.view',
    },
    children: [
      buildModalRoute(
        {
          path: 'edit/general',
          name: routeNames.customerEditGeneral,
          meta: {
            requiresAuth: true,
            permission: 'customers.update',
          },
          component: () => import('./views/CustomerEditGeneralView.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'edit/details',
          name: routeNames.customerEditDetails,
          meta: {
            requiresAuth: true,
            permission: 'customers.update',
          },
          component: () => import('./views/CustomerEditDetailsView.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'edit/custom-invoice-fields',
          name: routeNames.customerEditCustomInvoiceFields,
          meta: {
            requiresAuth: true,
            permission: 'customers.update',
          },
          component: () =>
            import('./views/CustomerEditCustomInvoiceFieldsView.vue'),
        },
        { fullscreen: false, width: 'md' }
      ),
      buildModalRoute(
        {
          path: 'edit/running-balance/:locationId(\\d+)',
          name: routeNames.customerEditRunningBalance,
          meta: {
            requiresAuth: true,
            permission: 'customers.update',
          },
          component: () => import('./views/CustomerEditRunningBalanceView.vue'),
        },
        { fullscreen: false }
      ),
      buildModalRoute(
        {
          path: 'balance-transactions/:locationId(\\d+)',
          name: routeNames.customerBalanceTransactions,
          meta: {
            requiresAuth: true,
            permission: 'customers.view',
          },
          component: () =>
            import('./views/CustomerBalanceTransactionsView.vue'),
        },
        { fullscreen: false, width: 'xl' }
      ),
    ],
  },
];

export const customerRoutesFactory = {
  customerDetails(customerId: number | string): RouteLocationRaw {
    return {
      name: routeNames.customerDetails,
      params: {
        customerId,
      },
    };
  },
  customerEditGeneral(customerId: number | string): RouteLocationRaw {
    return {
      name: routeNames.customerEditGeneral,
      params: {
        customerId,
      },
    };
  },
  customerEditDetails(customerId: number | string): RouteLocationRaw {
    return {
      name: routeNames.customerEditDetails,
      params: {
        customerId,
      },
    };
  },
  customerEditCustomInvoiceFields(
    customerId: number | string
  ): RouteLocationRaw {
    return {
      name: routeNames.customerEditCustomInvoiceFields,
      params: {
        customerId,
      },
    };
  },
  customerEditRunningBalance(
    customerId: number | string,
    locationId: number
  ): RouteLocationRaw {
    return {
      name: routeNames.customerEditRunningBalance,
      params: {
        customerId,
        locationId,
      },
    };
  },
  customerBalanceTransactions(
    customerId: number | string,
    locationId: number
  ): RouteLocationRaw {
    return {
      name: routeNames.customerBalanceTransactions,
      params: {
        customerId,
        locationId,
      },
    };
  },
};
